// Named haptic patterns. On Android (and any browser implementing the Web
// Vibration API) we use navigator.vibrate. iOS Safari has never implemented
// navigator.vibrate, even from an installed PWA — but Safari 17.4+ fires a
// taptic on click of a <label> wrapping <input type="checkbox" switch>. We
// use that trick to provide haptics on iPhone.
//
// The user-facing on/off flag lives in AppSettings; on app boot, App.tsx
// calls setHapticsEnabled() to seed it. We cache the flag in module state
// so haptic firing stays fully synchronous (no async settings read in the
// click path).

const supportsVibrate = typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';

// Coarse-pointer + no-vibrate is the iOS-Safari signature on the devices we
// care about. Desktop Safari is fine pointer; Android Chrome has vibrate.
const supportsCoarsePointer =
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(pointer: coarse)').matches;

let enabled = true;

export function setHapticsEnabled(value: boolean): void {
  enabled = value;
}

export function areHapticsEnabled(): boolean {
  return enabled;
}

// iOS Safari implements neither navigator.vibrate nor any programmatic haptic —
// the ONLY way to buzz is a real user toggle of a real <input switch>. This
// signature (coarse pointer, no vibrate) is the platform where buttons must
// carry a hidden switch instead of calling haptics.* from a click handler.
export function usesSwitchHaptics(): boolean {
  return !supportsVibrate && supportsCoarsePointer;
}

// iOS Safari 17.4+: click on a <label> wrapping an <input switch> emits a
// taptic. The click MUST go through the label — clicking the input directly
// does nothing.
//
// Critically, the switch has to be *rendered* (laid out) for Safari to fire the
// taptic. A `display:none` element, or one attached to <head> (never laid out),
// produces no haptic at all — which is exactly why iOS haptics were silent. So
// we attach it to <body>, kept imperceptible via off-screen position + zero
// opacity (still laid out, unlike display:none), and defer removal so the
// taptic isn't cancelled by an immediate detach.
export function fireIOSHaptic(): void {
  if (typeof document === 'undefined' || !document.body) return;
  try {
    const label = document.createElement('label');
    label.setAttribute('aria-hidden', 'true');
    label.style.cssText =
      'position:fixed;top:0;left:0;width:1px;height:1px;opacity:0;pointer-events:none;z-index:-2147483648;';
    const input = document.createElement('input');
    input.type = 'checkbox';
    input.setAttribute('switch', '');
    label.appendChild(input);
    document.body.appendChild(label);
    label.click();
    setTimeout(() => label.remove(), 0);
  } catch {
    // ignore
  }
}

function fireIOSBurst(count: number, spacingMs: number): void {
  fireIOSHaptic();
  for (let i = 1; i < count; i++) {
    setTimeout(fireIOSHaptic, i * spacingMs);
  }
}

function fire(
  vibratePattern: number | number[],
  iosBurstCount: number,
  iosSpacingMs: number = 120,
): void {
  if (!enabled) return;
  if (supportsVibrate) {
    try {
      navigator.vibrate(vibratePattern);
    } catch {
      // Some browsers rate-limit vibrate and throw on excess calls.
    }
    return;
  }
  if (supportsCoarsePointer) {
    fireIOSBurst(iosBurstCount, iosSpacingMs);
  }
}

export const haptics = {
  /** Generic button press — short and crisp. */
  tap: () => fire(12, 1),
  /** Generic secondary press — quieter than tap. */
  softTap: () => fire(8, 1),
  /** Note acknowledgment — distinct double-blip. */
  notice: () => fire([6, 40, 6], 2),
  /** Crossing a section/segment boundary or expanding a flexible loop. */
  boundary: () => fire([10, 50, 18], 2),
  /** Generic state change for toggles / nav. */
  transition: () => fire(6, 1),
  /** Pattern complete — celebratory triple. */
  success: () => fire([20, 60, 20, 60, 30], 3),
  /** Tracker: advance a row. Heavy double-thump — the most prominent haptic.
   *  iOS: 4 taptics at 70ms spacing — fires close enough to stack into one
   *  perceived "punch" instead of separate taps. */
  rowNext: () => fire([170, 40, 140], 4, 70),
  /** Tracker: step back a row. Tick-then-thunk — feels reversed vs rowNext. */
  rowPrev: () => fire([25, 70, 70], 2),
  /** Tracker: pause the active session. Strong two-beat with clear gap. */
  sessionPause: () => fire([80, 120, 80], 2),
};
